import { ref } from 'vue'

import { useSupportStore } from '@/store/support'

interface SearchItem {
  tenant: string
  published_at: string
  read_minute_duration: number
  title: string
  summary: string
  slug: string
  url: string
  type: string
}

interface SearchList {
  data: SearchItem[]
  page: number
  page_size: number
  total: number
  total_pages: number
}

interface SuggestionItem {
  tenant: string
  title: string
  slug: string
  url: string
  type: string
}

interface FlatArticle {
  id: number
  slug: string
  title: string
  tags: string[]
  categoryName: string
  categorySlug: string
}

function flattenArticles(store: ReturnType<typeof useSupportStore>): FlatArticle[] {
  const cats = store.categories || []
  const out: FlatArticle[] = []
  for (const c of cats) {
    for (const a of c.articles ?? []) {
      out.push({
        id: a.id,
        slug: a.slug,
        title: a.title,
        tags: a.tags ?? [],
        categoryName: c.name,
        categorySlug: c.slug,
      })
    }
    for (const sc of c.subcategories ?? []) {
      for (const a of sc.articles ?? []) {
        out.push({
          id: a.id,
          slug: a.slug,
          title: a.title,
          tags: a.tags ?? [],
          categoryName: c.name,
          categorySlug: c.slug,
        })
      }
    }
  }
  return out
}

function matches(article: FlatArticle, q: string): boolean {
  const needle = q.toLowerCase()
  if (article.title.toLowerCase().includes(needle)) return true
  if (article.slug.toLowerCase().includes(needle)) return true
  if (article.tags.some((t) => t.toLowerCase().includes(needle))) return true
  return false
}

const PAGE_SIZE = 10
const MAX_SUGGESTIONS = 8

export function useSearchList() {
  const supportStore = useSupportStore()
  const searchList = ref<SearchList | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<unknown>(null)

  const fetchSearchList = async (q: string = '', _tenant: string = '', page: number = 1) => {
    try {
      if (!supportStore.fetched) {
        await supportStore.fetchSupportCategories()
      }
      const all = flattenArticles(supportStore)
      const matched = q ? all.filter((a) => matches(a, q)) : all
      const total = matched.length
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
      const slice = matched.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      searchList.value = {
        data: slice.map((a) => ({
          tenant: a.categoryName,
          published_at: '',
          read_minute_duration: 0,
          title: a.title,
          summary: '',
          slug: a.slug,
          url: `/${a.categorySlug}/${a.slug}`,
          type: 'content',
        })),
        page,
        page_size: PAGE_SIZE,
        total,
        total_pages: totalPages,
      }
    } catch (e) {
      error.value = e
    } finally {
      loading.value = false
    }
  }

  return { searchList, fetchSearchList, loading, error }
}

export function useSuggestionList() {
  const supportStore = useSupportStore()
  const suggestionList = ref<SuggestionItem[]>([])
  const loading = ref<boolean>(true)
  const error = ref<unknown>(null)

  const fetchSuggestionList = async (q: string = '') => {
    try {
      if (q.length <= 3) {
        suggestionList.value = []
        return
      }
      if (!supportStore.fetched) {
        await supportStore.fetchSupportCategories()
      }
      const matched = flattenArticles(supportStore).filter((a) => matches(a, q))
      suggestionList.value = matched.slice(0, MAX_SUGGESTIONS).map((a) => ({
        tenant: a.categoryName,
        title: a.title,
        slug: a.slug,
        url: `/${a.categorySlug}/${a.slug}`,
        type: 'content',
      }))
    } catch (e) {
      error.value = e
    } finally {
      loading.value = false
    }
  }

  return { suggestionList, fetchSuggestionList, loading, error }
}
